import React, { useState, Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { motion } from 'framer-motion'
import {
  XMarkIcon,
  DocumentTextIcon,
  TableCellsIcon,
  DocumentArrowDownIcon,
  CheckIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'
import { type ExportFormat, type ExportConfig, exportReport } from '@/utils/exportUtils'
import toast from 'react-hot-toast'

interface ExportOption {
  format: ExportFormat
  label: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  color: string
  bgColor: string
}

const exportOptions: ExportOption[] = [
  {
    format: 'pdf',
    label: 'PDF Document',
    description: 'Professional report with branding, ideal for printing and sharing',
    icon: DocumentArrowDownIcon,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
  {
    format: 'xlsx',
    label: 'Excel Spreadsheet',
    description: 'Editable spreadsheet with multiple sheets for detailed analysis',
    icon: TableCellsIcon,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  {
    format: 'csv',
    label: 'CSV File',
    description: 'Simple comma-separated values for data import and processing',
    icon: DocumentTextIcon,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
]

interface ExportModalProps {
  isOpen: boolean
  onClose: () => void
  config: ExportConfig
  title?: string
}

const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  config,
  title = 'Export Report',
}) => {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('pdf')
  const [isExporting, setIsExporting] = useState(false)

  const handleExport = async () => {
    setIsExporting(true)
    try {
      await exportReport(selectedFormat, config)
      toast.success(`Report exported as ${selectedFormat.toUpperCase()} successfully!`)
      onClose()
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export report. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-white flex items-center gap-2"
                    >
                      <ArrowDownTrayIcon className="h-5 w-5" />
                      {title}
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="rounded-full p-1 text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <p className="text-primary-100 text-sm mt-1">
                    Choose your preferred export format
                  </p>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Report Info */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900">{config.title}</h4>
                    {config.subtitle && (
                      <p className="text-sm text-gray-600 mt-1">{config.subtitle}</p>
                    )}
                    {config.dateRange && (
                      <p className="text-xs text-gray-500 mt-2">
                        Period: {config.dateRange.from} to {config.dateRange.to}
                      </p>
                    )}
                    {config.institution?.name && (
                      <p className="text-xs text-gray-500 mt-1">
                        Institution: {config.institution.name}
                      </p>
                    )}
                  </div>

                  {/* Format Options */}
                  <div className="space-y-3">
                    {exportOptions.map((option) => {
                      const Icon = option.icon
                      const isSelected = selectedFormat === option.format

                      return (
                        <motion.button
                          key={option.format}
                          onClick={() => setSelectedFormat(option.format)}
                          className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                            isSelected
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-lg ${option.bgColor}`}>
                              <Icon className={`h-6 w-6 ${option.color}`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium text-gray-900">
                                  {option.label}
                                </h4>
                                {isSelected && (
                                  <div className="p-1 rounded-full bg-primary-500">
                                    <CheckIcon className="h-4 w-4 text-white" />
                                  </div>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 mt-1">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </motion.button>
                      )
                    })}
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleExport}
                    disabled={isExporting}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isExporting ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        Export {selectedFormat.toUpperCase()}
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default ExportModal
